import { ExclamationTriangleIcon, LockOpenIcon } from "@heroicons/react/24/outline";

import Button from "~/components/buttons/Button/Button";
import type {
	StaffingUnlock,
	UpdateStaffingUnlocksPayload,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { StaffingUnlockList } from "~/modules/billing/components/InvoiceDetailsView/components/StaffingUnlockForm.tsx";
import { useStaffingUnlockSelection } from "~/modules/billing/components/InvoiceDetailsView/components/useStaffingUnlockSelection.ts";

type Props = {
	/** Without it the section only explains. A user who cannot manage invoices gets no form. */
	onSubmit?: (payloads: UpdateStaffingUnlocksPayload[]) => void;
	staffingUnlocks: StaffingUnlock[];
};

export function UnlockedStaffingsSection({ onSubmit, staffingUnlocks }: Props) {
	const { payloads, selectNone, selection, toggleStaffing } = useStaffingUnlockSelection(staffingUnlocks);

	return <div className="space-y-4">
		<div className="flex items-center justify-between gap-4">
			<div className="flex items-center gap-2">
				<span className="flex size-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
					<LockOpenIcon className="size-5" />
				</span>
				<h3 className="text-base font-semibold text-gray-900">Erfassungen entsperrt / Rechnungsvorgang
					pausiert</h3>
			</div>
			{onSubmit && <div className="flex items-center gap-3">
				<Button type="button"
						theme="white"
						size="sm"
						disabled={selection.size === 0}
						onClick={selectNone}>
					Alle auswählen
				</Button>
				<Button type="button"
						theme="primary"
						size="sm"
						disabled={payloads.length === 0}
						onClick={() => onSubmit(payloads)}>
					{selection.size === 0 ? "Alle Staffings erneut sperren" : "Änderungen speichern"}
				</Button>
			</div>}
		</div>
		<div className={onSubmit ? "grid gap-8 lg:grid-cols-2" : ""}>
			<div className="space-y-4 text-sm text-gray-700">
				<div className="space-y-2">
					<p>Das Bearbeiten und Erstellen von Erfassungen wurde für die entsperrten Staffings dieser Rechnung
						temporär aktiviert, um nachträgliche Änderungen zu ermöglichen.</p>
					<p>Die Rechnungsstellung kann erst fortgesetzt werden, sobald alle Änderungen vorgenommen wurden und
						die
						Erfassungen aller Staffings wieder gesperrt sind.</p>
				</div>
				<div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
					<div className="flex items-center gap-2">
						<ExclamationTriangleIcon className="size-5 shrink-0 text-amber-600" />
						<h4 className="font-semibold">Nach der Sperrung</h4>
					</div>
					<p>Bei der Sperrung wird <strong>nur die Summe der Erfassungen</strong> neu berechnet,
						<strong> nicht der Rechnungsbetrag</strong>. Bitte prüfe nach der Sperrung im Abschnitt
						&quot;Rechnungsbetrag&quot;, ob die Summe der Einzelpositionen noch zur Summe der Erfassungen
						passt,
						und <strong>passe sie bei Bedarf selbst an</strong>.</p>
					<p>Lade danach das <strong>Rechnungsdokument erneut herunter</strong>, damit es alle Änderungen an
						den
						Erfassungen und am Rechnungsbetrag enthält.</p>
				</div>
			</div>
			{onSubmit && <div>
				<StaffingUnlockList staffingUnlocks={staffingUnlocks}
									selection={selection}
									onToggle={toggleStaffing} />
			</div>}
		</div>
	</div>;
}
